import useApiQuery from '@/hooks/useApiQuery';
import useLiveData from '@/hooks/useLiveData';
import useApiDelete from '@/hooks/useApiDelete';
import useTitle from '@/hooks/useTitle';
import {
  Avatar,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  IconButton,
  Chip,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import ToastMessage from '@/components/toasts/ToastMessage';
import { useRouteLoaderData } from 'react-router-dom';
import { useState, useMemo, useEffect, useCallback } from 'react';
import useAppStore from '@/store/app';
import { Delete, AccessTime } from '@mui/icons-material';
import ListView from '@/components/datagrid/ListView';
import useSearchStore from '@/store/search';
import useApiMutation from '@/hooks/useApiMutation';
import api from '@/services/ApiService';
import qs from 'qs';
import * as XLSX from 'xlsx';

import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import RedoOutlinedIcon from '@mui/icons-material/RedoOutlined';
import CreditScoreOutlinedIcon from '@mui/icons-material/CreditScoreOutlined';
import { ApiAggregation } from '@/components/datagrid/columns/ApiAggregation';

export default function TimeEntries() {
  useTitle('Zeitbuchungen');
  const { liveData } = useLiveData('timeRecord');
  const {
    users,
    me: {
      _id: meId,
      role: { permissions = [], superuser = false },
    },
  } = useRouteLoaderData('auth');
  const [selectedRows, setSelectedRows] = useState([]);
  const { selectedItem, setSelectedItem } = useAppStore();

  const [isOvertime, setIsOvertime] = useState(
    permissions?.timeRecord?.update?.approvedAt === true ? true : false,
  );
  const [isWeekend, setIsWeekend] = useState(false);
  const [isApproved, setIsApproved] = useState(
    permissions?.timeRecord?.update?.billed === true ? true : false,
  );
  const [billedFilter, setBilledFilter] = useState(
    permissions?.timeRecord?.update?.approvedAt === true
      ? 'unbilled'
      : 'neutral',
  );
  const [showOnlyOwn, setShowOnlyOwn] = useState(false);

  const canSeeAllRecords =
    superuser || permissions?.timeRecord?.update?.approvedAt === true;

  const [searchQuery, setSearchQuery] = useState('');
  const { registerLocalSearch, unregisterLocalSearch } = useSearchStore();

  useEffect(() => {
    registerLocalSearch({
      label: 'Zeitbuchungen',
      onSearch: (query) => setSearchQuery(query),
      onClear: () => setSearchQuery(''),
    });

    return () => {
      unregisterLocalSearch();
    };
  }, [registerLocalSearch, unregisterLocalSearch]);

  const mongoFilter = useMemo(() => {
    const filter = { $and: [] };

    if (searchQuery) {
      filter.$and.push({
        $or: [
          { title: { $regex: searchQuery, $options: 'i' } },
          { description: { $regex: searchQuery, $options: 'i' } },
        ],
      });
    }
    if (isOvertime) {
      filter.$and.push({ isOvertime: true });
    }
    if (isWeekend) {
      filter.$and.push({ isWeekend: true });
    }
    if (isApproved) {
      filter.$and.push({ approvedAt: { $type: 'date' } });
    }
    if (billedFilter === 'unbilled') {
      filter.$and.push({ billed: { $ne: true } });
    }
    if (billedFilter === 'billed') {
      filter.$and.push({ billed: true });
    }
    if (showOnlyOwn || !canSeeAllRecords) {
      filter.$and.push({ user: meId });
    }
    if (filter.$and.length === 0) {
      return {};
    }
    return filter;
  }, [
    searchQuery,
    isOvertime,
    isWeekend,
    isApproved,
    billedFilter,
    showOnlyOwn,
    canSeeAllRecords,
    meId,
  ]);

  const { mutateAsync: deleteTimeRecord } = useApiDelete('timeRecord');

  const {
    data: timeRecords,
    isPending,
    refetch,
  } = useApiQuery('timeRecord', mongoFilter, true);

  const [totalTimeData, setTotalTimeData] = useState({
    totalMs: 0,
    loading: true,
  });
  const fetchTotalTime = useCallback(async () => {
    setTotalTimeData((prev) => ({ ...prev, loading: true }));
    try {
      const queryString = qs.stringify(
        { ...mongoFilter, limit: 10000 },
        { encode: true, arrayFormat: 'indices', allowDots: false },
      );
      const {
        data: { result = [] },
      } = await api.get(`/timeRecord?${queryString}`);

      const totalMs = result.reduce((acc, record) => {
        if (record.startDate && record.endDate) {
          return acc + (new Date(record.endDate) - new Date(record.startDate));
        }
        return acc;
      }, 0);

      setTotalTimeData({ totalMs, loading: false });
    } catch (error) {
      console.error('Error fetching total time:', error);
      setTotalTimeData({ totalMs: 0, loading: false });
    }
  }, [mongoFilter]);

  useEffect(() => {
    fetchTotalTime();
  }, [fetchTotalTime]);

  const formattedTotalTime = useMemo(() => {
    const totalMinutes = Math.floor(totalTimeData.totalMs / 1000 / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  }, [totalTimeData.totalMs]);

  const { mutateAsync: updateTimeRecord } = useApiMutation('timeRecord');

  const handleDelete = () => {
    selectedRows.forEach((id) => {
      if (selectedItem === id) {
        setSelectedItem(null);
      }
      deleteTimeRecord(id);
    });
    setSelectedRows([]);
  };

  const handleBilling = async () => {
    const canBill =
      superuser || permissions?.timeRecord?.update?.billed === true;

    const toastId = toast.info(
      <ToastMessage
        title="Abrechnung"
        subtitle="Daten werden vorbereitet..."
      />,
      { autoClose: false, closeOnClick: false, isLoading: true },
    );

    try {
      const queryString = qs.stringify(mongoFilter, {
        encode: true,
        arrayFormat: 'indices',
        allowDots: false,
      });

      const {
        data: { result = [] },
      } = await api.get(`/timeRecord?${queryString}`);

      if (result.length === 0) {
        toast.update(toastId, {
          render: (
            <ToastMessage title="Fehler" subtitle="Keine Einträge gefunden." />
          ),
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        return;
      }

      const uniquePartyIds = [
        ...new Set(result.map((r) => r.partyId).filter(Boolean)),
      ];
      const partyCache = {};
      await Promise.all(
        uniquePartyIds.map(async (id) => {
          try {
            const { data } = await api.get(`/party/${id}`);
            partyCache[id] =
              `${data.company || ''} ${data.company2 || ''}`.trim();
          } catch (e) {
            partyCache[id] = 'Unbekannter Kunde';
          }
        }),
      );

      const generateAndShowDownload = (currentResults) => {
        const detailedData = currentResults.map((record) => {
          const user = users.find((u) => u._id === record.user);
          const start = new Date(record.startDate);
          const end = new Date(record.endDate);
          const durationHours = (end - start) / 1000 / 60 / 60;
          const userName =
            `${user?.firstName || ''} ${user?.lastName || ''}`.trim();

          return {
            Name: userName,
            Datum: start.toLocaleDateString('de-DE'),
            Arbeitszeitstart: start.toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit',
            }),
            Arbeitszeitende: end.toLocaleTimeString('de-DE', {
              hour: '2-digit',
              minute: '2-digit',
            }),
            Dauer: durationHours.toFixed(2).replace('.', ','),
            Wochenendarbeit: record.isWeekend ? 'Ja' : 'Nein',
            Kunde: record.partyId
              ? partyCache[record.partyId] || 'Interner Kunde'
              : 'Interner Kunde',
            Ticket: record.ticketReference || '',
            'Buchung verrechnet': record.billed ? 'Ja' : 'Nein',
            _rawDuration: durationHours,
            _userName: userName,
          };
        });

        const summaryMap = detailedData.reduce((acc, curr) => {
          acc[curr._userName] = (acc[curr._userName] || 0) + curr._rawDuration;
          return acc;
        }, {});

        const summaryData = Object.keys(summaryMap).map((name) => ({
          Mitarbeiter: name,
          'Gesamtdauer (Stunden)': summaryMap[name]
            .toFixed(2)
            .replace('.', ','),
        }));

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(
            detailedData.map(({ _rawDuration, _userName, ...rest }) => rest),
          ),
          'Details',
        );
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(summaryData),
          'Zusammenfassung',
        );

        toast.update(toastId, {
          isLoading: false,
          type: 'success',
          render: (
            <Box fullWidth>
              <ToastMessage
                title="DownloadExcel"
                subtitle="Die Datei steht nun zum Download bereit."
              />
              <Button
                variant="outlined"
                color="primary"
                fullWidth
                size="small"
                startIcon={<CloudDownloadOutlinedIcon />}
                sx={{ mt: 2 }}
                onClick={() => {
                  XLSX.writeFile(
                    workbook,
                    `Abrechnung_${new Date().toISOString().split('T')[0]}.xlsx`,
                  );
                  toast.dismiss(toastId);
                }}
              >
                Herunterladen
              </Button>
            </Box>
          ),
        });
      };

      if (canBill) {
        toast.update(toastId, {
          isLoading: false,
          type: 'info',
          render: (
            <Box>
              <ToastMessage
                title="Abrechnungs-Optionen"
                subtitle="Möchten Sie die Einträge als ausbezahlt markieren?"
              />
              <Box
                sx={{ mt: 2, display: 'flex', flexDirection: 'row', gap: 1 }}
              >
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<CreditScoreOutlinedIcon />}
                  fullWidth
                  size="small"
                  onClick={async () => {
                    toast.update(toastId, {
                      render: <ToastMessage title="Markiere Einträge..." />,
                      isLoading: true,
                    });
                    await Promise.allSettled(
                      result.map((record) =>
                        updateTimeRecord(
                          { billed: true },
                          'timeRecord',
                          record._id,
                          false,
                          true,
                        ),
                      ),
                    );
                    refetch();
                    const updatedResults = result.map((r) => ({
                      ...r,
                      billed: true,
                    }));
                    generateAndShowDownload(updatedResults);
                  }}
                >
                  Abrechnen
                </Button>
                <Button
                  color="default"
                  variant="outlined"
                  size="small"
                  onClick={() => generateAndShowDownload(result)}
                >
                  <RedoOutlinedIcon />
                </Button>
              </Box>
            </Box>
          ),
        });
      } else {
        generateAndShowDownload(result);
      }
    } catch (error) {
      console.error(error);
      toast.update(toastId, {
        render: (
          <ToastMessage title="Fehler" subtitle="Aktion fehlgeschlagen." />
        ),
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const columns = [
    {
      field: 'user',
      headerName: 'Mitarbeiter:in',
      align: 'center',
      minWidth: 250,
      renderCell: ({ row }) => {
        const currentUser = users.find((user) => user._id === row.user);
        return (
          <Box
            sx={{
              display: 'flex',
              flexFlow: 'row',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Avatar
              src={
                currentUser?.profilePicture &&
                `${import.meta.env.VITE_FILE_URL}/${currentUser?.profilePicture}`
              }
              sx={{ width: '32px', height: '32px', mr: 1 }}
            />
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Typography variant="body2">
                {`${currentUser?.firstName} ${currentUser?.lastName}`}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'partyId',
      headerName: 'Kunde',
      flex: 1,
      minWidth: 130,
      renderCell: ({ row }) => (
        <ApiAggregation
          id={row.partyId}
          resource="party"
          mapResponse={(data) =>
            data && data?.partyType === 'PERSON'
              ? `${data.firstName || ''} ${data.lastName || ''}`.trim()
              : `${data.company || ''}${data.company2 ? ` ${data.company2}` : ''}`.trim()
          }
          placeholder="—"
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Beschreibung',
      flex: 2,
    },
    {
      field: 'startDate',
      headerName: 'Geleiset am',
      minWidth: 130,

      valueGetter: (value) =>
        new Date(value).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        }),
    },
    {
      field: 'duration',
      headerName: 'Dauer',
      renderCell: ({ row }) => {
        const { startDate, endDate } = row;
        if (startDate && endDate) {
          const duration = new Date(endDate) - new Date(startDate);
          const hours = Math.floor(duration / 1000 / 60 / 60);
          const minutes = Math.floor((duration / 1000 / 60) % 60);
          return `${hours ? `${hours}h ` : ''}${minutes}m`;
        }
        return '';
      },
    },
    {
      field: 'approvedAt',
      headerName: 'Genehmigt am',
      minWidth: 130,

      valueGetter: (value) =>
        value
          ? new Date(value).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
    },
    {
      field: 'approvedBy',
      headerName: 'Genehmigt von',
      align: 'center',
      minWidth: 250,
      renderCell: ({ row }) => {
        const currentUser = users.find((user) => user._id === row.approvedBy);
        return (
          currentUser && (
            <Box
              sx={{
                display: 'flex',
                flexFlow: 'row',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Avatar
                src={
                  currentUser?.profilePicture &&
                  `${import.meta.env.VITE_FILE_URL}/${currentUser?.profilePicture}`
                }
                sx={{ width: '32px', height: '32px', mr: 1 }}
              />
              <Box
                sx={{
                  display: 'flex',
                  flexFlow: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Typography variant="body2">
                  {`${currentUser?.firstName} ${currentUser?.lastName}`}
                </Typography>
              </Box>
            </Box>
          )
        );
      },
    },
  ];

  const actions = [];

  actions.push(
    <Chip
      key="total-time"
      icon={
        totalTimeData.loading ? (
          <CircularProgress size={17} color="inherit" />
        ) : (
          <AccessTime />
        )
      }
      label={
        totalTimeData.loading ? 'Berechne...' : `Gesamt: ${formattedTotalTime}`
      }
      variant="outlined"
      color="primary"
      sx={{ alignSelf: 'center' }}
    />,
  );

  if (
    superuser ||
    (permissions?.timeRecord?.update?.approvedAt === true &&
      permissions?.timeRecord?.update?.approvedBy === true)
  ) {
    actions.push(
      <Button
        disabled={
          !selectedRows.length ||
          selectedRows.every(
            (row) => timeRecords.find((tr) => tr._id === row)?.approvedAt,
          )
        }
        color="primary"
        onClick={async () => {
          if (!selectedRows.length) return;
          const toastId = toast.info(
            <ToastMessage
              title="Genehmigung läuft"
              subtitle="Bitte warten..."
            />,
            { autoClose: false, closeOnClick: false, isLoading: true },
          );
          const results = await Promise.allSettled(
            selectedRows.map((id) => {
              return updateTimeRecord(
                {
                  approvedAt: new Date(),
                  approvedBy: meId,
                },
                'timeRecord',
                id,
                false,
                true,
              );
            }),
          );
          let success = 0;
          let failed = 0;
          let forbidden = 0;
          let otherErrors = [];
          results.forEach((res) => {
            if (res.status === 'fulfilled') {
              success++;
            } else {
              const code =
                res.reason?.response?.data?.code ||
                res.reason?.code ||
                res.reason?.status;
              if (code === 403) {
                forbidden++;
              } else {
                failed++;
                otherErrors.push(
                  res.reason?.message ||
                    res.reason?.response?.data?.message ||
                    'Unbekannter Fehler',
                );
              }
            }
          });
          toast.dismiss(toastId);
          if (forbidden > 0) {
            toast.error(
              <ToastMessage
                title="Keine Rechte"
                subtitle="Du hast keine Berechtigung, um einen oder mehrere Einträge zu genehmigen."
              />,
            );
          }
          if (failed > 0) {
            toast.error(
              <ToastMessage
                title="Fehler beim Genehmigen"
                subtitle={`Bei ${failed} Einträgen ist ein Fehler aufgetreten: ${otherErrors.join(', ')}`}
              />,
            );
          }
          if (success > 0) {
            toast.success(
              <ToastMessage
                title="Genehmigt"
                subtitle={`${success} Einträge erfolgreich genehmigt${failed > 0 ? `, ${failed} fehlgeschlagen` : ''}${forbidden > 0 ? `, ${forbidden} keine Rechte` : ''}.`}
              />,
            );
          }
        }}
      >
        Genehmigen
      </Button>,
    );
  }

  if (
    superuser ||
    (permissions?.timeRecord?.update?.approvedAt === true &&
      permissions?.timeRecord?.update?.approvedBy === true)
  ) {
    actions.push(
      <Button key="billing-action" color="primary" onClick={handleBilling}>
        Export
      </Button>,
      <Box flexGrow={1} />,
    );
  }
  if (canSeeAllRecords) {
    actions.push(
      <ToggleButtonGroup
        key="own-toggle"
        value={showOnlyOwn ? ['own'] : []}
        onChange={(_, newVals) => setShowOnlyOwn(newVals.includes('own'))}
        aria-label="Eigene"
        exclusive={false}
        size="small"
      >
        <ToggleButton
          value="own"
          selected={showOnlyOwn}
          aria-label="Eigene"
          variant={showOnlyOwn ? 'contained' : 'outlined'}
          color={showOnlyOwn ? 'primary' : 'default'}
        >
          Eigene
        </ToggleButton>
      </ToggleButtonGroup>,
    );
  }
  if (
    superuser ||
    (permissions?.timeRecord?.update?.approvedAt === true &&
      permissions?.timeRecord?.update?.approvedBy === true)
  ) {
    actions.push(
      <ToggleButtonGroup
        key="billed-filter"
        value={billedFilter}
        onChange={(_, newVal) => setBilledFilter(newVal || 'neutral')}
        aria-label="Bezahlfilter"
        exclusive
        size="small"
      >
        <ToggleButton
          value="unbilled"
          selected={billedFilter === 'unbilled'}
          aria-label="Unbezahlt"
          variant={billedFilter === 'unbilled' ? 'contained' : 'outlined'}
          color={billedFilter === 'unbilled' ? 'primary' : 'default'}
        >
          Unbezahlt
        </ToggleButton>

        <ToggleButton
          value="neutral"
          selected={billedFilter === 'neutral'}
          aria-label="Alle"
          variant={billedFilter === 'neutral' ? 'contained' : 'outlined'}
          color={billedFilter === 'neutral' ? 'primary' : 'default'}
        >
          Alle
        </ToggleButton>

        <ToggleButton
          value="billed"
          selected={billedFilter === 'billed'}
          aria-label="Bezahlt"
          variant={billedFilter === 'billed' ? 'contained' : 'outlined'}
          color={billedFilter === 'billed' ? 'primary' : 'default'}
        >
          Bezahlt
        </ToggleButton>
      </ToggleButtonGroup>,
    );
  } else {
    actions.push(<Box flexGrow={1} />);
  }

  actions.push(
    <ToggleButtonGroup
      value={isOvertime ? ['overtime'] : []}
      onChange={(_, newVals) => setIsOvertime(newVals.includes('overtime'))}
      aria-label="Überstunden"
      exclusive={false}
      size="small"
    >
      <ToggleButton
        value="overtime"
        selected={isOvertime}
        aria-label="Überstunden"
        variant={isOvertime ? 'contained' : 'outlined'}
        color={isOvertime ? 'primary' : 'default'}
      >
        Überstunden
      </ToggleButton>
    </ToggleButtonGroup>,

    <ToggleButtonGroup
      value={isApproved ? ['approved'] : []}
      onChange={(_, newVals) => setIsApproved(newVals.includes('approved'))}
      aria-label="Genehmigt"
      exclusive={false}
      size="small"
    >
      <ToggleButton
        value="approved"
        selected={isApproved}
        aria-label="Genehmigt"
        variant={isApproved ? 'contained' : 'outlined'}
        color={isApproved ? 'primary' : 'default'}
      >
        Genehmigt
      </ToggleButton>
    </ToggleButtonGroup>,

    <ToggleButtonGroup
      value={isWeekend ? ['weekend'] : []}
      onChange={(_, newVals) => setIsWeekend(newVals.includes('weekend'))}
      aria-label="Wochenende"
      exclusive={false}
      size="small"
    >
      <ToggleButton
        value="weekend"
        selected={isWeekend}
        aria-label="Wochenende"
        variant={isWeekend ? 'contained' : 'outlined'}
        color={isWeekend ? 'primary' : 'default'}
      >
        Wochenende
      </ToggleButton>
    </ToggleButtonGroup>,

    {
      title: 'Löschen',
      label: 'Löschen',
      color: 'error',
      selection: 'multi',
      icon: Delete,
      onClick: handleDelete,
    },
  );

  return (
    <ListView
      key={JSON.stringify(mongoFilter)}
      rows={timeRecords || []}
      columns={columns}
      actions={actions}
      loading={isPending}
      setSelectedRows={setSelectedRows}
      selectedRows={selectedRows}
      refetch={refetch}
      livedata={liveData}
    />
  );
}
